import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Search, X, Link2, Pencil, Plus } from 'lucide-react';
import { useFundraiserStore } from '../store/fundraiserStore';
import { LinkFundraiserModal } from '../components/competitors/LinkFundraiserModal';
import { EditFundraiserModal } from '../components/competitors/EditFundraiserModal';
import { ScreenshotThumb } from '../components/competitors/ScreenshotThumb';
import { AddFundraiserModal } from '../components/organizations/AddFundraiserModal';
import type { CompetitorFundraiser, CompetitorPlatform, CampaignStatus, DiscoverySource, FundraiserFilters } from '../types';

const PLATFORM_LABELS: Record<CompetitorPlatform, string> = {
  VERTICAL_RAISE: 'Vertical Raise',
  SNAP_RAISE: 'Snap Raise',
  ETEAM_SPONSOR: 'ETeamSponsor',
  SCHOOL_FUNDR: 'SchoolFundr',
};

const PLATFORM_BADGE_COLORS: Record<CompetitorPlatform, string> = {
  VERTICAL_RAISE: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  SNAP_RAISE: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  ETEAM_SPONSOR: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  SCHOOL_FUNDR: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
};

const SOURCE_LABELS: Record<DiscoverySource, string> = {
  TWITTER: 'Scraped',
  FACEBOOK: 'Scraped',
  INSTAGRAM: 'Scraped',
  CSV_IMPORT: 'Import',
  MANUAL: 'Manual',
};

const SOURCE_COLORS: Record<DiscoverySource, string> = {
  TWITTER: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  FACEBOOK: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  INSTAGRAM: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  CSV_IMPORT: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  MANUAL: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
};

const STATUS_BADGE_COLORS: Record<CampaignStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  ENDED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  UNKNOWN: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
};

function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export const Competitors = () => {
  const navigate = useNavigate();
  const {
    fundraisers,
    pagination,
    filters,
    isLoading,
    stats,
    isLoadingStats,
    fetchFundraisers,
    fetchStats,
    setFilters,
    setPage,
  } = useFundraiserStore();

  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [localFilters, setLocalFilters] = useState<FundraiserFilters>(filters);
  const [linkingFundraiserId, setLinkingFundraiserId] = useState<string | null>(null);
  const [isAddFundraiserOpen, setIsAddFundraiserOpen] = useState(false);
  const [editingFundraiser, setEditingFundraiser] = useState<CompetitorFundraiser | null>(null);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  const addFundraiserButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    fetchFundraisers();
    fetchStats();
  }, [fetchFundraisers, fetchStats]);

  // Deterministic focus return after an edit-triggered refresh: the list's
  // <tbody> unmounts/remounts while isLoading is true (see the table below),
  // so the pencil that was clicked no longer exists as a DOM node until the
  // refresh completes. Wait for isLoading to clear, then focus the same
  // row's pencil by its stable data attribute — falling back to the Add
  // Fundraiser button when the edited row left the current filter/page.
  useEffect(() => {
    if (!pendingFocusId || isLoading) return;
    const btn = document.querySelector<HTMLButtonElement>(`button[data-fundraiser-id="${pendingFocusId}"]`);
    (btn ?? addFundraiserButtonRef.current)?.focus();
    setPendingFocusId(null);
  }, [pendingFocusId, isLoading]);

  const applyFilters = useCallback(() => {
    setFilters({ ...localFilters, search: searchInput || undefined });
  }, [localFilters, searchInput, setFilters]);

  const clearFilters = useCallback(() => {
    setSearchInput('');
    setLocalFilters({});
    setFilters({});
  }, [setFilters]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') applyFilters();
  };

  const hasActiveFilters = !!(filters.platform || filters.state || filters.sport || filters.year || filters.status || filters.sourcePlatform || filters.search || filters.isMatched !== undefined);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Competitor Intelligence
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Track competitor fundraisers across platforms
          </p>
        </div>
        <button
          ref={addFundraiserButtonRef}
          onClick={() => setIsAddFundraiserOpen(true)}
          className="btn-primary flex items-center gap-1.5 whitespace-nowrap"
        >
          <Plus className="h-4 w-4" />
          Add Fundraiser
        </button>
      </div>

      {/* Stats row */}
      {stats && !isLoadingStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="card p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Fundraisers</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {stats.totalCampaigns.toLocaleString()}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Raised</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(stats.totalRaised)}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">Platforms</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {Object.keys(stats.byPlatform).length}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="School name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="input pl-10"
              />
            </div>
          </div>

          {/* Platform */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Platform</label>
            <select
              value={localFilters.platform || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, platform: e.target.value as CompetitorPlatform || undefined })}
              className="input"
            >
              <option value="">All</option>
              {Object.entries(PLATFORM_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          {/* State */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">State</label>
            <input
              type="text"
              placeholder="TX"
              maxLength={2}
              value={localFilters.state || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, state: e.target.value.toUpperCase() || undefined })}
              className="input w-20"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
            <select
              value={localFilters.status || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, status: e.target.value as CampaignStatus || undefined })}
              className="input"
            >
              <option value="">All</option>
              <option value="ACTIVE">Active</option>
              <option value="ENDED">Ended</option>
              <option value="UNKNOWN">Unknown</option>
            </select>
          </div>

          {/* Source */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Source</label>
            <select
              value={localFilters.sourcePlatform || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, sourcePlatform: e.target.value as DiscoverySource || undefined })}
              className="input"
            >
              <option value="">All</option>
              <option value="TWITTER">Scraped</option>
              <option value="CSV_IMPORT">Import</option>
              <option value="MANUAL">Manual</option>
            </select>
          </div>

          {/* Apply / Clear */}
          <div className="flex gap-2">
            <button onClick={applyFilters} className="btn-primary text-sm">
              Apply
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="btn-secondary text-sm">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">School</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">State</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sport</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Platform</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Year</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Raised</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Source</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blast-600 mx-auto"></div>
                  </td>
                </tr>
              ) : fundraisers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No fundraisers found
                  </td>
                </tr>
              ) : (
                fundraisers.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-sm">
                      {f.organizationId ? (
                        <button
                          onClick={() => navigate(`/organizations/${f.organizationId}`)}
                          className="text-blast-600 dark:text-blast-400 hover:underline font-medium text-left"
                        >
                          {f.organization?.name || f.schoolName}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900 dark:text-gray-100">{f.schoolName}</span>
                          <button
                            onClick={() => setLinkingFundraiserId(f.id)}
                            className="text-xs text-gray-400 hover:text-blast-600 dark:hover:text-blast-400"
                            title="Link to CRM organization"
                          >
                            <Link2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{f.state || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{f.sport || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PLATFORM_BADGE_COLORS[f.platform]}`}>
                        {PLATFORM_LABELS[f.platform]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{f.year || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(f.raisedAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {f.status && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE_COLORS[f.status]}`}>
                          {f.status.toLowerCase()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SOURCE_COLORS[f.sourcePlatform]}`}>
                        {SOURCE_LABELS[f.sourcePlatform]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        <ScreenshotThumb fundraiser={f} />
                        {f.campaignUrl && (
                          <a
                            href={f.campaignUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Open campaign page"
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <ExternalLink className="h-4 w-4 inline" />
                          </a>
                        )}
                        <button
                          type="button"
                          aria-label="Edit fundraiser"
                          data-fundraiser-id={f.id}
                          onClick={(e) => {
                            e.currentTarget.focus();
                            setEditingFundraiser(f);
                          }}
                          className="text-gray-400 hover:text-blast-600 dark:hover:text-blast-400"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.totalCount)} of {pagination.totalCount}
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(pagination.page - 1)}
                disabled={!pagination.hasPrevPage}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPage(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Link Fundraiser Modal — relink can change raised totals, so it now
          refreshes stats too (page-owned refresh, CRM-161). */}
      <LinkFundraiserModal
        isOpen={!!linkingFundraiserId}
        fundraiser={fundraisers.find(f => f.id === linkingFundraiserId) || null}
        onClose={() => setLinkingFundraiserId(null)}
        onSuccess={() => {
          setLinkingFundraiserId(null);
          fetchFundraisers();
          fetchStats();
        }}
      />

      {/* Add Fundraiser Modal — no-context "easy add" (CRM-143). The modal
          owns the success toast; refresh both the list and the stats tiles
          since a new row can land outside the current filters/page. */}
      <AddFundraiserModal
        isOpen={isAddFundraiserOpen}
        onClose={() => setIsAddFundraiserOpen(false)}
        onSuccess={() => {
          setIsAddFundraiserOpen(false);
          fetchFundraisers();
          fetchStats();
        }}
      />

      {/* Edit Fundraiser Modal (CRM-161) — page-owned refresh: fires both
          fetchFundraisers and fetchStats once each, then the effect above
          restores focus to the edited row's pencil (or the Add Fundraiser
          button, if the row left the current filter/page) once loading clears. */}
      <EditFundraiserModal
        isOpen={!!editingFundraiser}
        fundraiser={editingFundraiser}
        onClose={() => setEditingFundraiser(null)}
        onSuccess={() => {
          const id = editingFundraiser?.id ?? null;
          setEditingFundraiser(null);
          setPendingFocusId(id);
          fetchFundraisers();
          fetchStats();
        }}
        onScreenshotsChanged={fetchFundraisers}
      />
    </div>
  );
};
